const TAB_TYPES = { all: 'all', coin: 'coin', fiat: 'fiat', commodity: 'commodity' };

function requestUrl(state, query, tab) {
  const base = state.root.dataset.converterNexus || state.root.dataset.nexusBase || window.arzConstants?.NEXUS_BASEURL || '';
  const params = new URLSearchParams({ type: TAB_TYPES[tab] || 'all' });
  if (tab !== 'fiat' || query) {
    params.set('limit', '20');
    params.set('offset', '0');
  }
  if (query) params.set('search', query);
  if (!base) throw new Error('Nexus URL is unavailable');
  return `${base.replace(/\/$/, '')}/v1/pub/coins?${params.toString()}`;
}

function setLoading(state, visible, ownerId = state.searchRequestId) {
  if (ownerId !== state.searchRequestId) return;
  const loading = state.dialog?.querySelector('[data-converter-search-loading]');
  loading?.classList.toggle('arz-tw-hidden', !visible);
  loading?.classList.toggle('arz-tw-flex', visible);
}

export function normalizeAsset(asset, tab) {
  const metrics = asset?.metrics?.cmc || {};
  return {
    ...asset,
    type: asset?.type || (tab === 'all' ? '' : TAB_TYPES[tab]),
    usd: asset?.usd ?? metrics.price,
    toman: asset?.toman ?? metrics.price_irt,
    price_change_percent_24h: asset?.price_change_percent_24h ?? metrics.price_change_percent_24h ?? 0,
    price_change_percent_irt_24h: asset?.price_change_percent_irt_24h ?? metrics.price_change_percent_irt_24h ?? null,
    market_cap: asset?.market_cap ?? metrics.market_cap ?? 0,
    dominance: asset?.dominance ?? metrics.dominance ?? 0,
    rank: asset?.rank ?? 0,
  };
}

export function isCompleteAsset(asset) {
  return Boolean(asset?.id && asset.slug && asset.type
    && Number(asset.usd) > 0 && Number(asset.toman) > 0);
}

export function abortSearch(state) {
  if (state.searchTimer) window.clearTimeout(state.searchTimer);
  state.searchTimer = 0;
  if (state.searchController) state.searchController.abort();
  state.searchController = null;
  state.searchRequestId += 1;
  setLoading(state, false);
}

export async function loadAssets(state, query, tab, renderResults) {
  const trimmed = query.trim();
  const cacheKey = `${tab}:${trimmed.toLocaleLowerCase()}`;
  const cache = trimmed ? state.searchCache : state.defaultCache;
  if (cache.has(cacheKey)) {
    renderResults(cache.get(cacheKey), 'ready');
    return;
  }

  const requestId = ++state.searchRequestId;
  const controller = new AbortController();
  state.searchController = controller;
  setLoading(state, true, requestId);
  renderResults([], 'loading');

  try {
    const response = await fetch(requestUrl(state, trimmed, tab), {
      signal: controller.signal,
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Asset search failed: ${response.status}`);
    const payload = await response.json();
    if (requestId !== state.searchRequestId) return;
    const responseAssets = Array.isArray(payload.data)
      ? payload.data
      : payload.data && typeof payload.data === 'object'
        ? Object.values(payload.data)
        : [];
    const assets = responseAssets.map((asset) => normalizeAsset(asset, tab)).filter(isCompleteAsset);
    cache.set(cacheKey, assets);
    renderResults(assets, assets.length ? 'ready' : 'empty');
  } catch (error) {
    if (error.name !== 'AbortError' && requestId === state.searchRequestId) renderResults([], 'error');
  } finally {
    if (requestId === state.searchRequestId) {
      state.searchController = null;
      setLoading(state, false, requestId);
    }
  }
}

export function scheduleSearch(state, value, renderResults) {
  abortSearch(state);
  const trimmed = value.trim();
  if (!trimmed) {
    const cacheKey = `${state.activeTab}:`;
    renderResults(state.defaultCache.get(cacheKey) || [], 'ready');
    return;
  }
  state.searchTimer = window.setTimeout(() => {
    state.searchTimer = 0;
    loadAssets(state, value.trim(), state.activeTab, renderResults);
  }, 500);
}
